import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { ReminderType } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Reminder Types',
    description: 'Manage reminder types',
    permissionModuleKey: 'reminder_type',
    route: {
      list: '/reminder_type',
      new: '/reminder_type/new',
      detail: (id: number) => `/reminder_type/${id}`,
      edit: (id: number) => `/reminder_type/${id}/edit`,
    },
    api: {
      list: '/p-crm-reminder-type',
      detail: (id: number) => `/p-crm-reminder-type/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search reminder types...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pr_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pr_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: ReminderType) => item.pr_descr,
        options: [],
      },
    ],
    applyFilter: (items: ReminderType[], filters: { pr_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pr_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pr_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pr_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Reminder Type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/reminder_type/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ReminderType) => {
      window.location.href = `/reminder_type/${item.id}`;
    },
    keyExtractor: (item: ReminderType) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new reminder type.',
    noSearchResults: 'No reminder types found matching your search criteria.',
    noFilterResults: 'No reminder types match the selected filters.',
  },
  delete: {
    title: 'Delete Reminder Type',
    message: 'Are you sure you want to delete this reminder type? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete reminder type. Please try again.',
    handler: async (id: number) => {
      const { deleteReminderType } = await import('../../../services/mutations');
      await deleteReminderType(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Reminder Type Details',
    notFoundTitle: 'Reminder Type not found',
    notFoundMessage: 'The reminder type you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Reminder Types',
    defaultTitle: 'Unnamed Reminder Type',
    getTitle: (item: ReminderType) => {
      return item.pr_descr || `Reminder Type #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Reminder Type',
      subtitle: 'Add a new reminder type',
      submitText: 'Create Reminder Type',
    },
    edit: {
      title: 'Edit Reminder Type',
      subtitle: 'Update reminder type information',
      submitText: 'Update Reminder Type',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading reminder type...',
    errorMessage: 'Failed to save reminder type. Please try again.',
    create: async (data: Partial<ReminderType>) => {
      const { createReminderType } = await import('../../../services/mutations');
      return await createReminderType(data);
    },
    update: async (id: number, data: Partial<ReminderType>) => {
      const { updateReminderType } = await import('../../../services/mutations');
      return await updateReminderType(id, data);
    },
  },
  fields: [
    {
      name: 'pr_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter reminder type description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Reminder Type Information',
        icon: '📝',
        fields: ['pr_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<ReminderType>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pr_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: ReminderType,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.pr_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: ReminderType) => {
  return null;
};

export const getInitialFormData = (): Partial<ReminderType> => {
  return {
    pr_descr: '',
  };
};

export const mapItemToFormData = (item: ReminderType): Partial<ReminderType> => {
  return {
    pr_descr: item.pr_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: ReminderType,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

